import { Directive, HostListener, OnInit, inject, input } from '@angular/core';
import { DrawerComponent } from '@progress/kendo-angular-layout';
import { merge } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { TodoEntryModel } from '../../../core/models/todo-planner/todo-entry.model';
import { DrawerService } from '../../../core/services/ui/drawer.service';
import { DragOrResizeInteraction } from '../../../shared/data-types/interact.types';
import { TodoPlannerService } from '../services/todo-planner.service';
import { TodoEntryDragService } from '../todo-entry/services/drag-and-drop/todo-entry-drag.service';
import { TodoEntryService } from '../todo-entry/services/todo-entry.service';
import { TodoEntryCompletedCheck } from '../utils/todo-planner.constants';

@Directive({
  selector: '[appTodoEntryInteraction]',
})
export class TodoEntryInteractionDirective implements OnInit {
  readonly todoEntryModel = input.required<TodoEntryModel>();

  // DI
  private readonly todoEntryDragService = inject(TodoEntryDragService);
  private readonly todoPlannerService = inject(TodoPlannerService);
  private readonly drawerService = inject(DrawerService);

  private isMousePressed = false;
  private drawer: DrawerComponent = null;

  ngOnInit(): void {
    this.todoPlannerService.drawer$
      .pipe(filter((drawer) => drawer !== null))
      .subscribe((drawer) => {
        this.drawer = drawer;
      });
  }

  /**
   * Fires when user clicks on a planner event.
   */
  @HostListener('mousedown', ['$event'])
  public mouseDown(event: MouseEvent): void {
    event.stopPropagation();
    const src = event.target as HTMLElement | null;

    // Ignore click on "completed" checkbox.
    if (src?.closest(`.${TodoEntryCompletedCheck}`)) {
      return;
    }

    this.isMousePressed = true;
  }

  @HostListener('mouseup')
  public mouseUp(): void {
    if (
      this.isMousePressed &&
      this.todoEntryDragService.interaction === DragOrResizeInteraction.None
    ) {
      this.drawer.toggle();

      const expand$ = this.drawer.expand.pipe(take(1));
      const collapse$ = this.drawer.collapse.pipe(take(1));

      merge(expand$, collapse$).subscribe(() => {
        this.drawerService.toggleDrawerContentWidth$.next();
        if (this.drawer.expanded) {
          const clonedTodoEntry = TodoEntryService.cloneTodoModel(this.todoEntryModel(), false);
          this.todoPlannerService.sendTodoEntryModel$.next(clonedTodoEntry);
          return;
        }
      });

      this.isMousePressed = false;
    }

    this.todoEntryDragService.interaction = DragOrResizeInteraction.None;
  }

  @HostListener('mousemove', ['$event'])
  public stopMouseMovePropagation(event: MouseEvent): void {
    event.stopPropagation(); // Prevents the event from bubbling up to the event container.
  }
}
